import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { proposedEditNavigationStateSchema } from '@/features/controller/configuration/schemas'
import type { ProposedEditNavigationState } from '@/features/controller/configuration/types'

/** What becomes of a proposed edit that arrives in the navigation state. */
export type ArrivingProposal =
  | { kind: 'none' }
  /**
   * `baseReplaced`: the proposal was made against an older version than the one shown, or rebased after a
   * conflict, so saving it overwrites a version someone else saved. The review warns, and the user decides.
   */
  | { kind: 'accepted'; edit: ProposedEditNavigationState['proposedEdit']; baseReplaced: boolean }

function judgeArrivingProposal(navigationState: unknown, currentVersionId: string): ArrivingProposal {
  const parsed = proposedEditNavigationStateSchema.safeParse(navigationState)
  if (!parsed.success) {
    return { kind: 'none' }
  }
  const { proposedEdit } = parsed.data
  return {
    kind: 'accepted',
    edit: proposedEdit,
    baseReplaced: proposedEdit.baseReplaced === true || proposedEdit.previousVersionId !== currentVersionId,
  }
}

/**
 * Reads a proposed edit from the navigation state, judged once against the version shown on mount.
 */
export function useArrivingProposal(currentVersionId: string): ArrivingProposal {
  const { state } = useLocation()
  const [arrivingProposal] = useState(() => judgeArrivingProposal(state, currentVersionId))

  useEffect(() => {
    // So that a reload does not apply the proposal again
    if (arrivingProposal.kind !== 'none') {
      globalThis.history.replaceState({}, '')
    }
  }, [])

  return arrivingProposal
}
